import React, { useEffect, useState } from 'react';

import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { attorneysService } from '../../../services/attorneysService';
import { practiceAreasService } from '../../../services/practiceAreasService';

const AttorneysManager = () => {
	const [attorneys, setAttorneys] = useState([]);
	const [practiceAreas, setPracticeAreas] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [selectedAttorney, setSelectedAttorney] = useState(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);
	const [activeTab, setActiveTab] = useState('basic'); // 'basic', 'education', 'practice-areas'
	const [attorneyEducation, setAttorneyEducation] = useState([]);
	const [attorneySpecializations, setAttorneySpecializations] = useState([]);
	const [attorneyAdmissions, setAttorneyAdmissions] = useState([]);
	const [formData, setFormData] = useState({
		name: '',
		title: '',
		bio: '',
		image_url: '',
		is_partner: false,
		is_featured: false,
		display_order: 0,
		level: 'partner',
		status: 'Available',
		languages: [],
		professional_membership: '',
		location: '',
	});
	const [educationForm, setEducationForm] = useState({
		school: '',
		degree: '',
		year: new Date().getFullYear(),
	});
	const [admissionForm, setAdmissionForm] = useState({
		jurisdiction: '',
		admission_year: new Date().getFullYear(),
	});
	const [selectedPracticeArea, setSelectedPracticeArea] = useState('');

	useEffect(() => {
		loadData();
	}, []);

	const loadData = async () => {
		try {
			setLoading(true);
			const [attorneysResult, practiceAreasResult] = await Promise.all([
				attorneysService.getAllAttorneys(),
				practiceAreasService.getAllPracticeAreas(),
			]);

			if (attorneysResult.error) {
				setError(attorneysResult.error);
			} else {
				setAttorneys(attorneysResult.data || []);
			}

			if (practiceAreasResult.error) {
				console.error('Error loading practice areas:', practiceAreasResult.error);
			} else {
				setPracticeAreas(practiceAreasResult.data || []);
			}
		} catch (error) {
			setError('Failed to load data');
			console.error('Error loading data:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleAddNew = () => {
		setFormData({
			name: '',
			title: '',
			bio: '',
			image_url: '',
			is_partner: false,
			is_featured: false,
			display_order: attorneys.length + 1,
			level: 'partner',
			status: 'Available',
			languages: [],
			professional_membership: '',
			location: '',
		});
		setIsEditMode(false);
		setSelectedAttorney(null);
		setIsModalOpen(true);
	};

	const handleEdit = async (attorney) => {
		setFormData({
			name: attorney.name || '',
			title: attorney.title || '',
			bio: attorney.bio || '',
			image_url: attorney.image_url || '',
			is_partner: attorney.is_partner || false,
			is_featured: attorney.is_featured || false,
			display_order: attorney.display_order || 0,
			level: attorney.level || 'partner',
			status: attorney.status || 'Available',
			languages: attorney.languages || [],
			professional_membership: attorney.professional_membership || '',
			location: attorney.location || '',
		});
		setIsEditMode(true);
		setSelectedAttorney(attorney);
		setActiveTab('basic');
		setIsModalOpen(true);

		// Load attorney-specific data
		await loadAttorneyData(attorney.id);
	};

	const loadAttorneyData = async (attorneyId) => {
		try {
			const [educationResult, specializationsResult, admissionsResult] = await Promise.all([
				attorneysService.getAttorneyEducation(attorneyId),
				attorneysService.getAttorneySpecializations(attorneyId),
				attorneysService.getAttorneyAdmissions(attorneyId),
			]);

			setAttorneyEducation(educationResult.data || []);
			setAttorneySpecializations(specializationsResult.data || []);
			setAttorneyAdmissions(admissionsResult.data || []);
		} catch (error) {
			console.error('Error loading attorney data:', error);
		}
	};

	const handleDelete = async (attorneyId) => {
		if (window.confirm('Are you sure you want to delete this attorney?')) {
			try {
				const { error } = await attorneysService.deleteAttorney(attorneyId);
				if (error) {
					console.error('Error deleting attorney:', error);
					alert('Failed to delete attorney. Please try again.');
				} else {
					alert('Attorney deleted successfully!');
					loadData(); // Reload data after deletion
				}
			} catch (error) {
				console.error('Error deleting attorney:', error);
				alert('Failed to delete attorney. Please try again.');
			}
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			if (isEditMode) {
				// Update existing attorney
				const { error } = await attorneysService.updateAttorney(selectedAttorney.id, formData);
				if (error) {
					console.error('Error updating attorney:', error);
					alert('Failed to update attorney. Please try again.');
					return;
				}
				alert('Attorney updated successfully!');
			} else {
				// Create new attorney
				const { error } = await attorneysService.createAttorney(formData);
				if (error) {
					console.error('Error creating attorney:', error);
					alert('Failed to create attorney. Please try again.');
					return;
				}
				alert('Attorney created successfully!');
			}
			setIsModalOpen(false);
			loadData();
		} catch (error) {
			console.error('Error saving attorney:', error);
			alert('An error occurred. Please try again.');
		}
	};

	const handleInputChange = (field, value) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	const handleLanguageChange = (value) => {
		const languages = value
			.split(',')
			.map((lang) => lang.trim())
			.filter(Boolean);
		setFormData((prev) => ({
			...prev,
			languages,
		}));
	};

	// Education management functions
	const handleAddEducation = async () => {
		if (!educationForm.school.trim()) {
			alert('Please enter a school name');
			return;
		}

		try {
			const { error } = await attorneysService.addAttorneyEducation(
				selectedAttorney.id,
				educationForm
			);
			if (error) {
				alert('Failed to add education record');
				return;
			}
			alert('Education record added successfully!');
			setEducationForm({ school: '', degree: '', year: new Date().getFullYear() });
			await loadAttorneyData(selectedAttorney.id);
		} catch (error) {
			console.error('Error adding education:', error);
			alert('Failed to add education record');
		}
	};

	const handleDeleteEducation = async (educationId) => {
		if (window.confirm('Are you sure you want to delete this education record?')) {
			try {
				const { error } = await attorneysService.deleteAttorneyEducation(educationId);
				if (error) {
					alert('Failed to delete education record');
					return;
				}
				alert('Education record deleted successfully!');
				await loadAttorneyData(selectedAttorney.id);
			} catch (error) {
				console.error('Error deleting education:', error);
				alert('Failed to delete education record');
			}
		}
	};

	// Practice area management functions
	const handleAddSpecialization = async (practiceAreaId) => {
		if (!practiceAreaId) return;

		try {
			const { error } = await attorneysService.addAttorneySpecialization(
				selectedAttorney.id,
				practiceAreaId
			);
			if (error) {
				alert('Failed to add practice area specialization');
				return;
			}
			alert('Practice area specialization added successfully!');
			setSelectedPracticeArea(''); // Reset selection
			await loadAttorneyData(selectedAttorney.id);
		} catch (error) {
			console.error('Error adding specialization:', error);
			alert('Failed to add practice area specialization');
		}
	};

	const handleDeleteSpecialization = async (specializationId) => {
		if (window.confirm('Are you sure you want to remove this practice area specialization?')) {
			try {
				const { error } = await attorneysService.deleteAttorneySpecialization(specializationId);
				if (error) {
					alert('Failed to remove practice area specialization');
					return;
				}
				alert('Practice area specialization removed successfully!');
				await loadAttorneyData(selectedAttorney.id);
			} catch (error) {
				console.error('Error deleting specialization:', error);
				alert('Failed to remove practice area specialization');
			}
		}
	};

	const handleDeleteAdmission = async (admissionId) => {
		if (window.confirm('Are you sure you want to delete this admission record?')) {
			try {
				const { error } = await attorneysService.deleteAttorneyAdmission(admissionId);
				if (error) {
					alert('Failed to delete admission record');
					return;
				}
				alert('Admission record deleted successfully!');
				await loadAttorneyData(selectedAttorney.id);
			} catch (error) {
				console.error('Error deleting admission:', error);
				alert('Failed to delete admission record');
			}
		}
	};

	if (loading) {
		return (
			<div className='flex items-center justify-center h-64'>
				<div className='text-center'>
					<Icon name='Loader' className='h-8 w-8 animate-spin text-[#800020] mx-auto mb-4' />
					<p className='text-gray-600'>Loading attorneys...</p>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className='bg-red-50 border border-red-200 rounded-lg p-6'>
				<div className='flex items-center'>
					<Icon name='AlertCircle' className='h-5 w-5 text-red-500 mr-2' />
					<p className='text-red-700'>{error}</p>
				</div>
				<Button
					variant='outline'
					onClick={loadData}
					className='mt-4'
					iconName='RefreshCw'
					iconPosition='left'
				>
					Retry
				</Button>
			</div>
		);
	}

	return (
		<div className='space-y-6'>
			{/* Header */}
			<div className='bg-white shadow rounded-lg p-6'>
				<div className='flex justify-between items-center'>
					<div>
						<h1 className='text-2xl font-bold text-gray-900'>Manage Attorneys</h1>
						<p className='text-gray-600'>Add, edit, and manage attorney profiles</p>
					</div>
					<Button
						variant='default'
						onClick={handleAddNew}
						iconName='Plus'
						iconPosition='left'
						className='bg-[#800020] hover:bg-[#800020]/90'
					>
						Add Attorney
					</Button>
				</div>
			</div>

			{/* Attorneys Grid */}
			<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
				{attorneys.map((attorney) => (
					<div key={attorney.id} className='bg-white shadow rounded-lg overflow-hidden'>
						<div className='aspect-w-3 aspect-h-4'>
							<Image
								src={attorney.image_url || '/assets/images/no_image.png'}
								alt={attorney.name}
								className='w-full h-48 object-cover'
							/>
						</div>
						<div className='p-6'>
							<div className='flex items-center justify-between mb-2'>
								<h3 className='text-lg font-semibold text-gray-900'>{attorney.name}</h3>
								<div className='flex space-x-1'>
									{attorney.is_partner && (
										<span className='px-2 py-1 bg-[#800020] text-white text-xs rounded-full'>
											Partner
										</span>
									)}
									{attorney.is_featured && (
										<span className='px-2 py-1 bg-[#DB954E] text-white text-xs rounded-full'>
											Featured
										</span>
									)}
								</div>
							</div>
							<p className='text-sm text-gray-600 mb-2'>{attorney.title}</p>
							<p className='text-sm text-gray-500 mb-4 line-clamp-3'>{attorney.bio}</p>
							<div className='flex justify-between items-center'>
								<div className='text-xs text-gray-400'>Order: {attorney.display_order}</div>
								<div className='flex space-x-2'>
									<Button
										variant='outline'
										size='sm'
										onClick={() => handleEdit(attorney)}
										iconName='Edit'
									>
										Edit
									</Button>
									<Button
										variant='outline'
										size='sm'
										onClick={() => handleDelete(attorney.id)}
										iconName='Trash2'
										className='text-red-600 hover:text-red-700'
									>
										Delete
									</Button>
								</div>
							</div>
						</div>
					</div>
				))}
			</div>

			{/* Modal */}
			{isModalOpen && (
				<div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
					<div className='bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto'>
						<div className='p-6'>
							<div className='flex justify-between items-center mb-6'>
								<h2 className='text-xl font-semibold text-gray-900'>
									{isEditMode ? `Edit Attorney: ${selectedAttorney?.name}` : 'Add New Attorney'}
								</h2>
								<Button variant='outline' onClick={() => setIsModalOpen(false)} iconName='X'>
									Close
								</Button>
							</div>

							{/* Tabs */}
							{isEditMode && (
								<div className='flex space-x-1 mb-6 border-b border-gray-200'>
									<button
										type='button'
										onClick={() => setActiveTab('basic')}
										className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
											activeTab === 'basic'
												? 'border-[#800020] text-[#800020]'
												: 'border-transparent text-gray-500 hover:text-gray-700'
										}`}
									>
										Basic Info
									</button>
									<button
										type='button'
										onClick={() => setActiveTab('education')}
										className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
											activeTab === 'education'
												? 'border-[#800020] text-[#800020]'
												: 'border-transparent text-gray-500 hover:text-gray-700'
										}`}
									>
										Education
									</button>
									<button
										type='button'
										onClick={() => setActiveTab('practice-areas')}
										className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
											activeTab === 'practice-areas'
												? 'border-[#800020] text-[#800020]'
												: 'border-transparent text-gray-500 hover:text-gray-700'
										}`}
									>
										Practice Areas
									</button>
								</div>
							)}

							{/* Basic Info Tab */}
							{activeTab === 'basic' && (
								<form onSubmit={handleSubmit} className='space-y-4'>
									<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
										<Input
											label='Name'
											value={formData.name}
											onChange={(e) => handleInputChange('name', e.target.value)}
											required
										/>
										<Input
											label='Title'
											value={formData.title}
											onChange={(e) => handleInputChange('title', e.target.value)}
											required
										/>
									</div>

									<Input
										label='Image URL'
										value={formData.image_url}
										onChange={(e) => handleInputChange('image_url', e.target.value)}
									/>

									<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
										<Select
											label='Level'
											value={formData.level}
											onChange={(e) => handleInputChange('level', e.target.value)}
											options={[
												{ value: 'partner', label: 'Partner' },
												{ value: 'senior_partner', label: 'Senior Partner' },
												{ value: 'associate', label: 'Associate' },
												{ value: 'senior_associate', label: 'Senior Associate' },
												{ value: 'counsel', label: 'Counsel' },
												{ value: 'of_counsel', label: 'Of Counsel' },
											]}
										/>
										<Select
											label='Status'
											value={formData.status}
											onChange={(e) => handleInputChange('status', e.target.value)}
											options={[
												{ value: 'Available', label: 'Available' },
												{ value: 'Busy', label: 'Busy' },
												{ value: 'On Leave', label: 'On Leave' },
											]}
										/>
									</div>

									<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
										<Input
											label='Location'
											value={formData.location}
											onChange={(e) => handleInputChange('location', e.target.value)}
										/>
										<Input
											label='Languages (comma-separated)'
											value={formData.languages.join(', ')}
											onChange={(e) => handleLanguageChange(e.target.value)}
										/>
									</div>

									<Input
										label='Professional Membership'
										value={formData.professional_membership}
										onChange={(e) => handleInputChange('professional_membership', e.target.value)}
									/>

									<Input
										label='Display Order'
										type='number'
										value={formData.display_order}
										onChange={(e) => handleInputChange('display_order', parseInt(e.target.value))}
									/>

									<div>
										<label className='block text-sm font-medium text-gray-700 mb-2'>Bio</label>
										<textarea
											className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#800020] focus:border-transparent'
											rows={4}
											value={formData.bio}
											onChange={(e) => handleInputChange('bio', e.target.value)}
										/>
									</div>

									<div className='flex space-x-4'>
										<label className='flex items-center'>
											<input
												type='checkbox'
												checked={formData.is_partner}
												onChange={(e) => handleInputChange('is_partner', e.target.checked)}
												className='mr-2'
											/>
											<span className='text-sm text-gray-700'>Partner</span>
										</label>
										<label className='flex items-center'>
											<input
												type='checkbox'
												checked={formData.is_featured}
												onChange={(e) => handleInputChange('is_featured', e.target.checked)}
												className='mr-2'
											/>
											<span className='text-sm text-gray-700'>Featured</span>
										</label>
									</div>

									<div className='flex justify-end space-x-3 pt-4'>
										<Button variant='outline' onClick={() => setIsModalOpen(false)}>
											Cancel
										</Button>
										<Button
											variant='default'
											type='submit'
											className='bg-[#800020] hover:bg-[#800020]/90'
										>
											{isEditMode ? 'Update Attorney' : 'Create Attorney'}
										</Button>
									</div>
								</form>
							)}

							{/* Education Tab */}
							{activeTab === 'education' && (
								<div className='space-y-6'>
									<div className='bg-gray-50 rounded-lg p-4'>
										<h3 className='text-lg font-semibold text-gray-900 mb-4'>
											Add Education Record
										</h3>
										<div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
											<Input
												label='School/University'
												value={educationForm.school}
												onChange={(e) =>
													setEducationForm((prev) => ({ ...prev, school: e.target.value }))
												}
												placeholder='e.g., Harvard Law School'
											/>
											<Input
												label='Degree'
												value={educationForm.degree}
												onChange={(e) =>
													setEducationForm((prev) => ({ ...prev, degree: e.target.value }))
												}
												placeholder='e.g., Juris Doctor'
											/>
											<Input
												label='Year'
												type='number'
												value={educationForm.year}
												onChange={(e) =>
													setEducationForm((prev) => ({ ...prev, year: parseInt(e.target.value) }))
												}
											/>
										</div>
										<Button
											variant='default'
											onClick={handleAddEducation}
											className='mt-4 bg-[#800020] hover:bg-[#800020]/90'
											iconName='Plus'
											iconPosition='left'
										>
											Add Education
										</Button>
									</div>

									<div>
										<h3 className='text-lg font-semibold text-gray-900 mb-4'>Education Records</h3>
										{attorneyEducation.length === 0 ? (
											<p className='text-gray-500 italic'>No education records found</p>
										) : (
											<div className='space-y-3'>
												{attorneyEducation.map((edu) => (
													<div
														key={edu.id}
														className='flex items-center justify-between bg-white border rounded-lg p-4'
													>
														<div>
															<h4 className='font-medium text-gray-900'>{edu.school}</h4>
															<p className='text-sm text-gray-600'>
																{edu.degree} ({edu.year})
															</p>
														</div>
														<Button
															variant='outline'
															size='sm'
															onClick={() => handleDeleteEducation(edu.id)}
															iconName='Trash2'
															className='text-red-600 hover:text-red-700'
														>
															Delete
														</Button>
													</div>
												))}
											</div>
										)}
									</div>
								</div>
							)}

							{/* Practice Areas Tab */}
							{activeTab === 'practice-areas' && (
								<div className='space-y-6'>
									<div className='bg-gray-50 rounded-lg p-4'>
										<h3 className='text-lg font-semibold text-gray-900 mb-4'>
											Add Practice Area Specialization
										</h3>
										<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
											<Select
												label='Select Practice Area'
												value={selectedPracticeArea}
												onChange={(value) => {
													setSelectedPracticeArea(value);
												}}
												options={[
													{ value: '', label: 'Choose a practice area...' },
													...practiceAreas
														.filter(
															(pa) =>
																!attorneySpecializations.some(
																	(spec) => spec.practice_area_id === pa.id
																)
														)
														.map((pa) => ({ value: pa.id, label: pa.title })),
												]}
											/>
											<Button
												variant='default'
												onClick={() => {
													if (selectedPracticeArea) {
														handleAddSpecialization(selectedPracticeArea);
													}
												}}
												disabled={!selectedPracticeArea}
												className='bg-[#800020] hover:bg-[#800020]/90 disabled:opacity-50'
												iconName='Plus'
												iconPosition='left'
											>
												Add Specialization
											</Button>
										</div>
									</div>

									<div>
										<h3 className='text-lg font-semibold text-gray-900 mb-4'>
											Practice Area Specializations
										</h3>
										{attorneySpecializations.length === 0 ? (
											<p className='text-gray-500 italic'>No practice area specializations found</p>
										) : (
											<div className='space-y-3'>
												{attorneySpecializations.map((spec) => (
													<div
														key={spec.id}
														className='flex items-center justify-between bg-white border rounded-lg p-4'
													>
														<div>
															<h4 className='font-medium text-gray-900'>
																{spec.practice_areas?.title}
															</h4>
															<p className='text-sm text-gray-600'>
																{spec.practice_areas?.description}
															</p>
														</div>
														<Button
															variant='outline'
															size='sm'
															onClick={() => handleDeleteSpecialization(spec.id)}
															iconName='Trash2'
															className='text-red-600 hover:text-red-700'
														>
															Remove
														</Button>
													</div>
												))}
											</div>
										)}
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default AttorneysManager;
